// SessionPicker — Phase 3
//
// 활성 에디터 탭의 세션 바인딩 표시 + 변경 dropdown.
// 위치: FileTabs 옆 (Phase 4 에서 위치/디자인 폴리싱 예정).
//
// 표시:
//   미바인딩         → "[세션 미선택 ▼]" (회색)
//   바인딩 idle     → "[세션: <라벨> · sid <SID> ▼]"
//   바인딩 running  → "[세션: <라벨> · 실행중 12.3s]" (dropdown 비활성)
//
// dropdown:
//   - 같은 connection 의 idle 세션 우선
//   - 다른 연결 세션 (sub-section)
//   - "탐색기에서 + 세션 추가" 안내 (목록 비어있을 때)
//   - 고르면 그 탭이 속한 프로젝트(없으면 기본 작업공간)의 탭 전부에 걸린다 — App 의 handleBindSessionToTab

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FiChevronDown, FiUser, FiActivity, FiSearch } from 'react-icons/fi';
import Elapsed from './Elapsed';
import styles from './SessionPicker.module.css';
import { sessionDisplayName, sessionSid, isSessionBusy } from '../utils/sessionDisplay';
import { getConnType, connTypeColorVars } from '../utils/connectionTypes';

function CategoryIcon({ category, className }) {
    const cls = className || '';
    if (category === 'system-collect') return <FiActivity className={cls} style={{ color: '#10b981' }} />;
    if (category === 'system-query')   return <FiSearch className={cls} style={{ color: '#8b5cf6' }} />;
    return <FiUser className={cls} style={{ color: '#3b82f6' }} />;
}

const SessionPicker = ({ activeFile, onBindSession }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const sessionsList = useSelector(s => (s.sessions && s.sessions.list) || []);
    const connections = useSelector(s => s.schema.connections || []);

    // 외부 클릭 시 dropdown 닫기
    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    // 활성 탭의 현재 세션 객체
    const boundSession = useMemo(() => {
        if (!activeFile?.sessionId) return null;
        return sessionsList.find(s => s.sessionId === activeFile.sessionId) || null;
    }, [activeFile, sessionsList]);

    // 바인딩된 세션의 connection (Phase 4 — Header 의 currentConnectionName 대체)
    const boundConn = useMemo(() => {
        if (!boundSession) return null;
        return connections.find(c => c.id === boundSession.connectionId) || null;
    }, [boundSession, connections]);
    const boundConnectionName = boundSession ? (boundConn?.name || boundSession.connectionId) : null;
    // 연결 유형(개발·운영·교육 …) — 머리줄 배지에 있던 것을 여기로 옮겼다(2026-10-14). 실행 직전에 「지금 운영인가」 가 보여야 한다
    const connMeta = boundConn ? getConnType(boundConn.connType) : null;

    // dropdown 가용 옵션 — 활성 탭의 connection 우선, 그 외 연결의 세션은 sub-section
    // 데이터소스 동기화/모니터링 collector 의 'system-*' 세션은 전용 세션이라 에디터 탭에 바인딩 불가.
    // 'user' 카테고리만 바인딩 후보 (category 미지정 legacy 도 user 로 간주).
    const groupedSessions = useMemo(() => {
        const tabConnId = boundSession?.connectionId || null;
        const same = []; const others = [];
        for (const s of sessionsList) {
            if (s.ephemeral) continue;
            // system-collect / system-query 등 시스템 전용 세션은 사용자가 바인딩 못 하게 제외
            if (s.category && s.category !== 'user') continue;
            (s.connectionId === tabConnId ? same : others).push(s);
        }
        return { same, others };
    }, [sessionsList, boundSession]);

    const isRunning = isSessionBusy(boundSession);
    const canOpenDropdown = !isRunning;
    // 활성 탭에 sessionId 가 set 되어 있는지 — boundSession (sessionsList 매칭 결과) 와 별개.
    // 폴링 race 로 sessionsList 가 늦게 sync 될 때, boundSession 만 보고 "미선택" 표기하면 거짓.
    const hasBindingOnTab = !!activeFile?.sessionId;

    const labelText = (() => {
        if (!hasBindingOnTab) return t('common:sp.noneSelected');
        if (!boundSession) {
            // tab 에는 binding 이 있지만 sessionsList 에 매칭 항목 없음 — 폴링 지연 또는 만료.
            // 목록에 없는 세션은 붙일 번호가 없다 — id 조각을 보이지 않는다
            return t('common:sp.sessionChecking');
        }
        const lbl = sessionDisplayName(boundSession, t);
        const connPart = boundConnectionName ? `${boundConnectionName} · ` : '';
        // SID 는 SID 가 있는 DB(Oracle·Tibero)에서만 — PostgreSQL·SQLite 에 붙이지 않는다
        const sid = sessionSid(boundSession);
        if (sid != null) return `${connPart}${lbl} · sid ${sid}`;
        return `${connPart}${lbl}`;
    })();

    const onSelect = (sessionId) => {
        if (!activeFile) return;
        onBindSession(activeFile.id, sessionId);   // 프로젝트 단위로 걸린다
        setOpen(false);
    };

    return (
        <div className={styles.wrapper} ref={ref}>
            <button
                type="button"
                className={`${styles.toggleBtn} ${hasBindingOnTab ? styles.toggleBtnBound : ''} ${isRunning ? styles.toggleBtnRunning : ''}`}
                onClick={() => canOpenDropdown && setOpen(o => !o)}
                disabled={!canOpenDropdown}
                title={isRunning ? t('common:sp.cannotChangeWhileRunning') : t('common:sp.change')}
            >
                {connMeta && (
                    <span className={styles.connType} style={connTypeColorVars(connMeta)}
                        title={t('connection:connType.badgeTitle', { label: t(connMeta.labelKey), desc: t(connMeta.descKey) })}>
                        <span className={styles.connTypeDot} />{t(connMeta.labelKey)}
                    </span>
                )}
                {boundSession && <CategoryIcon category={boundSession.category} className={styles.catIcon} />}
                <span className={styles.label}>{labelText}</span>
                {isRunning && boundSession?.currentQuery && (
                    <span className={styles.runningElapsed}>
                        {t('common:sp.running')} <Elapsed startedAt={boundSession.currentQuery.startedAt} />
                    </span>
                )}
                {!isRunning && <FiChevronDown className={styles.chevron} />}
            </button>
            {open && canOpenDropdown && (
                <div className={styles.dropdown}>
                    <SessionGroup
                        title={t('common:sp.sameConnection')}
                        items={groupedSessions.same}
                        connections={connections}
                        currentSessionId={boundSession?.sessionId}
                        onPick={onSelect}
                    />
                    {groupedSessions.others.length > 0 && (
                        <SessionGroup
                            title={t('common:sp.otherConnection')}
                            items={groupedSessions.others}
                            connections={connections}
                            currentSessionId={boundSession?.sessionId}
                            onPick={onSelect}
                        />
                    )}
                    {groupedSessions.same.length === 0 && groupedSessions.others.length === 0 && (
                        <div className={styles.empty}>
                            {t('common:sp.noSessions1')} <strong>{t('common:sp.howToAdd')}</strong>{t('common:sp.noSessions2')}
                        </div>
                    )}
                    {/* 고른 세션이 어디까지 걸리는지 말한다 — 탭마다 따로이던 시절과 다르다 */}
                    <div className={styles.projectFooter}>
                        <small>{activeFile?.projectId ? t('common:sp.appliesToProject') : t('common:sp.appliesToWorkspace')}</small>
                    </div>
                </div>
            )}
        </div>
    );
};

const SessionGroup = ({ title, items, connections, currentSessionId, onPick }) => {
    const { t } = useTranslation();
    if (items.length === 0) return null;
    const connNameById = {};
    connections.forEach(c => { connNameById[c.id] = c.name; });
    return (
        <div className={styles.group}>
            <div className={styles.groupTitle}>{title}</div>
            {items.map(s => {
                const isCurrent = s.sessionId === currentSessionId;
                const connName = connNameById[s.connectionId] || s.connectionId;
                const lbl = sessionDisplayName(s, t);
                const sid = sessionSid(s);
                // 실행 중 / 취소 중 세션은 바인딩 후보에서 비활성 — 다른 작업에 영향 줄 수 있음.
                const isBusy = isSessionBusy(s);
                return (
                    <button
                        key={s.sessionId}
                        type="button"
                        className={`${styles.option} ${isCurrent ? styles.optionCurrent : ''}`}
                        onClick={() => { if (!isBusy) onPick(s.sessionId); }}
                        disabled={isBusy}
                        title={isBusy ? t('common:sp.cannotBindRunning') : undefined}
                        style={isBusy ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
                    >
                        <CategoryIcon category={s.category} className={styles.catIcon} />
                        <span className={styles.optionLabel}>{lbl}</span>
                        {sid != null && <span className={styles.optionSid}>sid {sid}</span>}
                        <span className={styles.optionConn}>· {connName}</span>
                        <span className={`${styles.optionStatus} ${styles[`status_${s.status}`] || ''}`}>{s.status}</span>
                    </button>
                );
            })}
        </div>
    );
};

SessionPicker.propTypes = {
    activeFile: PropTypes.object,
    onBindSession: PropTypes.func.isRequired,
};

export default SessionPicker;
